import { z } from 'zod';
import { expectedPriceFields } from './expected-price.js';
import { pageQuerySchema } from './http.js';
import { membershipStatusSchema } from './membership.js';
import { passwordSchema } from './password.js';
import { userStatusSchema } from './user.js';
/**
 * THE MEMBER-ACCOUNT CONTRACT — S26 Phase 5, the administration surface for the people who work
 * for a tenant.
 *
 * IT HAS NOTHING TO DO WITH `./invite.ts`. That file is VISITOR pre-registration (S11): a
 * `Person` expecting a guest, an `Invite` row, a kiosk lookup code. This one is user accounts,
 * `TenantMembership` rows and provider identities. Milestone S26 Constraint 4 exists because the
 * two words collide in English and in nothing else, so every symbol here is spelled
 * `memberInvite…` / `MemberInvite…` and never a bare `invite`. Do not "tidy" that away.
 */
/* ------------------------------------------------------------------ role sets (S42-m4dr) */
/**
 * The scopes a role may be granted at. Spelled literally, like `roleAssignmentDTOSchema` in
 * `./auth.js`, because `contracts` may not import `authz` — keep identical to `SCOPE_TYPES`.
 */
export const roleScopeTypeSchema = z.enum(['TENANT', 'LOCATION']);
/**
 * One requested grant — the role editor (`PUT /members/:id/roles`) and the invite both submit
 * an array of these.
 *
 * `roleKey` is a bounded string here and a real role at the API: `validateRoleSet` in
 * `@instanexus/authz` is the authority on which roles exist, which scopes each allows, that a
 * LOCATION grant names a location and a TENANT grant does not, and that no grant is repeated.
 * Those refusals carry their own error codes (`ROLE_SCOPE_INVALID`, `ROLE_DUPLICATE`), which a
 * Zod refinement here would flatten into `VALIDATION_FAILED`.
 */
export const roleAssignmentInputSchema = z.object({
    roleKey: z.string().trim().min(1).max(64),
    scopeType: roleScopeTypeSchema,
    locationId: z.string().trim().min(1).max(64).nullable().optional(),
});
/** A member may hold at most this many grants in one submission. */
export const MAX_ROLE_ASSIGNMENTS_PER_MEMBER = 50;
/** At least one grant (Decision 1) and a bound so the body cannot be unbounded. */
export const roleAssignmentSetSchema = z
    .array(roleAssignmentInputSchema)
    .min(1)
    .max(MAX_ROLE_ASSIGNMENTS_PER_MEMBER);
/**
 * One of a member's grants, as the administration surface shows it.
 *
 * DELIBERATELY NOT `RoleAssignmentDTO` (`./auth.js`). That is the caller's OWN grant on
 * `GET /auth/me`, keyed `scopeId`, and the SPAs feed it straight into `can()`. This one is
 * another person's grant as an administrator reads it: the location is named (`locationName`,
 * `null` for a TENANT grant — or for a LOCATION grant whose location has since been deleted).
 */
export const memberRoleAssignmentDTOSchema = z.object({
    roleKey: z.string().min(1),
    scopeType: roleScopeTypeSchema,
    locationId: z.string().min(1).nullable(),
    locationName: z.string().min(1).nullable(),
});
/**
 * One person's membership of the caller's tenant, as the administration surface sees it.
 *
 * DELIBERATELY NOT `MembershipDTO`. That DTO's single `status` is the MEMBERSHIP status and it
 * has always silently stood in for two independent facts: whether the person belongs here
 * (`TenantMembership.status`) and whether their login works at all (`User.status`). Phase 5 gives
 * `User.status` its first writer, so the two can now genuinely diverge and a screen that showed
 * one of them would be lying about the other. `GET /api/web/memberships` and `MembershipDTO` are
 * untouched by this milestone — see S26-TASK-019's "Out of scope" for why both surfaces exist.
 *
 * `id` is the `TenantMembership` id, which is also the `:id` of
 * `PATCH /api/web/members/:id/status`. Tenant-scoped by construction: every row in a response
 * belongs to the caller's own tenant, so `tenantId` is deliberately absent (same reasoning as
 * `membershipDTOSchema`).
 */
export const memberDTOSchema = z.object({
    id: z.string().min(1),
    userId: z.string().min(1),
    email: z.string().email(),
    name: z.string().min(1),
    membershipStatus: membershipStatusSchema,
    userStatus: userStatusSchema,
    invitedAt: z.string().datetime(),
    acceptedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * S42-m4dr. Every grant this member holds in the caller's tenant, sorted by role then
     * location. Also what `GET /api/web/members/:id` (the member detail) answers with.
     */
    roles: z.array(memberRoleAssignmentDTOSchema),
    /**
     * S42-m4dr Phase 2. When the member's invite link stops working — `null` unless the membership
     * is `INVITED` and a link is outstanding. MAY BE IN THE PAST: an expired invite is still a
     * pending one, and the admin screen must be able to say "expired — resend". `null` on an
     * `INVITED` row means no link is outstanding at all (it was revoked and the member later
     * reactivated) and a resend is the only way forward.
     */
    inviteExpiresAt: z.string().datetime().nullable(),
});
/* ------------------------------------------------------------------ member invite (TASK-017) */
/**
 * `POST /api/web/members/invite`.
 *
 * `roles` is a set of bounded strings HERE and real grants at the API. `packages/contracts` is
 * below `packages/authz` in the dependency graph (`authz` imports `contracts`, never the reverse
 * — `packages/authz/src/index.ts`), so this schema structurally cannot name the role vocabulary.
 * The authority is `validateRoleSet` + `canGrantAssignment` in the identity service — the same
 * two checks `PUT /members/:id/roles` runs, so `tenant.owner` is invitable exactly by those who
 * could grant it (S42-m4dr Decision 6).
 *
 * THE INVITER CHOOSES THE ROLES, AND ONLY HERE. `acceptMemberInviteRequestSchema` (S26-TASK-018)
 * has no role field and must never gain one — that absence is the whole defence against an
 * invitee escalating themselves.
 */
export const memberInviteRequestSchema = z.object({
    email: z.string().trim().email().max(320),
    name: z.string().trim().min(1).max(200),
    /** S42-m4dr Decision 6: the same role set, validated by the same rules, as the role editor. */
    roles: roleAssignmentSetSchema,
});
/**
 * The WIRE body of `POST /api/web/members/invite`: the invite (`memberInviteRequestSchema`, which
 * the invite form also resolves against) plus the amounts the admin was shown for the `+1` SEAT
 * preview (handoff 0010 §5 row 6: "when they accept, Directory rises …"). A tenant not billed per
 * seat previews — and confirms — `0` / `0`.
 */
export const memberInviteConfirmRequestSchema = memberInviteRequestSchema.extend(expectedPriceFields);
/* ------------------------------------------------------------------ accept invite (TASK-018) */
/**
 * `POST /api/web/auth/accept-invite`.
 *
 * NO `roleKey`, NO `name`, NO `tenantId` — AND NONE MAY EVER BE ADDED. The inviter chose the role
 * (`memberInviteRequestSchema`) and the display name, and the tenant is whichever one the
 * redeemed token's membership belongs to. A field here would be a field the invitee controls,
 * and the only interesting thing to do with it is grant yourself more than you were offered.
 * That absence is this milestone's entire answer to "an invited user must not be able to escalate
 * their own role".
 *
 * `email` accompanies `token` because the token is looked up by `(target, purpose)` — the
 * `@@index([target, purpose, createdAt])` on `VerificationCode` — and the model deliberately has
 * no join to `User` (`schema.prisma`: "an unknown target must be indistinguishable from a known
 * one"). It is not a second credential and proves nothing on its own: an attacker holding the
 * address without the token gets the same `VERIFICATION_CODE_INVALID` as one holding neither.
 *
 * `token` is opaque and bounded, exactly as `resetPasswordRequestSchema.code` is, and for the
 * same reason: `contracts` must not encode one issuer's format. 512 is generous headroom over
 * the 43 characters a base64url-encoded 256-bit token occupies.
 */
export const acceptMemberInviteRequestSchema = z.object({
    email: z.string().trim().email().max(320),
    token: z.string().trim().min(1).max(512),
    newPassword: passwordSchema,
});
/* ----------------------------------------------------------------- status update (TASK-019) */
/**
 * The two statuses an administrator may ASK FOR. Deliberately not `userStatusSchema`:
 * `PENDING` is written by the invite and by nothing else, and offering it here would let an
 * administrator push an accepted member back into a state that has no way out except a second
 * invite the boundary would then refuse (Constraint 3).
 *
 * `SUSPENDED` means "this person can no longer sign in, and their live token stops working on
 * its next request". `ACTIVE` means "lift that", and it restores the pair of statuses the member
 * had before — which for someone who never accepted their invite is `PENDING`/`INVITED`, not a
 * usable account. See `identity/member-status.ts` for the whole table; the RESPONSE always states
 * what actually happened, so a client must read `userStatus`/`membershipStatus` off the returned
 * `MemberDTO` rather than assume the requested value was applied verbatim.
 */
export const MEMBER_STATUS_TARGETS = ['ACTIVE', 'SUSPENDED'];
export const memberStatusTargetSchema = z.enum(MEMBER_STATUS_TARGETS);
export const memberStatusUpdateRequestSchema = z.object({
    status: memberStatusTargetSchema,
    /** Handoff 0010 §5 row 6: the amounts shown for the SEAT preview of this transition
     *  (`+1` to reactivate, `-1` to deactivate an ACTIVE member). A transition that moves no billed
     *  seat (withdrawing an unaccepted invite, a no-op) is not compared. */
    ...expectedPriceFields,
});
/* ------------------------------------------------------------------ role update (S42-m4dr) */
/**
 * `PUT /api/web/members/:id/roles` — REPLACE the member's whole role set.
 *
 * A replace, not a patch: the editor submits what the member should hold, and the API diffs it
 * against what they hold. Only the ADDED and REMOVED grants are checked against the caller's own
 * authority (Decision 2); an unchanged grant the caller could not have made is left alone.
 */
export const memberRolesUpdateRequestSchema = z.object({
    roles: roleAssignmentSetSchema,
});
/* ------------------------------------------------------------ list filter (S42-m4dr Phase 2) */
/**
 * `GET /api/web/members?status=…` — narrows the list by MEMBERSHIP status. Lower-case on the wire
 * like every other query value; `invited` is the "pending invites" view.
 */
export const MEMBER_LIST_STATUSES = ['invited', 'active', 'revoked'];
/** The `q` bounds on the members lists. Exported so a console can skip a draft the API would reject. */
export const MEMBER_SEARCH_MIN_LENGTH = 2;
export const MEMBER_SEARCH_MAX_LENGTH = 100;
/** A search box's draft as the `q` to send: trimmed, or `undefined` when too short to search. */
export function memberSearchTerm(draft) {
    const trimmed = draft.trim();
    return trimmed.length >= MEMBER_SEARCH_MIN_LENGTH ? trimmed : undefined;
}
/**
 * `q` is ONE term matched case-insensitively against the member's name OR email, with the same
 * `min(2)` floor as the tenant search (`tenantListQuerySchema`). It narrows rows and `meta.total`.
 */
export const memberListQuerySchema = pageQuerySchema.extend({
    status: z.enum(MEMBER_LIST_STATUSES).optional(),
    q: z.string().trim().min(MEMBER_SEARCH_MIN_LENGTH).max(MEMBER_SEARCH_MAX_LENGTH).optional(),
});
//# sourceMappingURL=member.js.map